import React from "react";
import { Skeleton } from "./SupernovaStates";
import { METRIC } from "./supernovaSummary";
import { EMPTY, fmtCount, fmtXp } from "./supernovaFormat";

/**
 * The four-tile stat strip: Owned · Staked · Strategy slots · XP balance.
 *
 * Every tile renders its metric's STATUS, not just its value. A tile whose read
 * failed, or whose backend isn't built, shows an em dash and says why — it never
 * shows 0, because 0 is a claim about the member's wallet.
 *
 * Two columns at mobile width, four from 900px up (supernova.css `.sn-stats`).
 * Every figure carries `.tnum` so the columns line up.
 */

const statusCaption = {
  [METRIC.IDLE]: "Connect a wallet",
  [METRIC.UNAVAILABLE]: "Not connected yet",
  [METRIC.ERROR]: "Couldn’t read this",
};

const StatTile = ({ label, metric, format = fmtCount, hint, footnote = null, testId }) => {
  const { status } = metric;
  const isReady = status === METRIC.READY;

  return (
    <div
      className="sn-stat"
      title={hint || metric.note || metric.message || undefined}
      data-testid={testId}
    >
      <p className="sn-stat-k">{label}</p>

      {status === METRIC.LOADING ? (
        <Skeleton className="h-[34px] w-[62%] max-w-[110px] mt-2.5 mb-1" />
      ) : (
        /* Gold only once there IS a figure — see `.sn-stat-v.sn-gold`. */
        <p className={`sn-stat-v tnum ${isReady ? "sn-gold" : ""}`}>
          {isReady ? format(metric.value) : EMPTY}
        </p>
      )}

      <p className={`sn-stat-n ${status === METRIC.ERROR ? "sn-bad" : ""}`}>
        {isReady ? metric.meta || " " : statusCaption[status] || " "}
      </p>
      {/* A second caption, for a tile whose figure is only PART of the answer —
          today that is XP, whose banked half isn't connected. */}
      {footnote && <p className="sn-stat-fn">{footnote}</p>}
    </div>
  );
};

/** "used / total" — either half can be unknown on its own. */
const SlotTile = ({ metric }) => {
  const isReady = metric.status === METRIC.READY;
  const slotMetric = isReady
    ? {
        ...metric,
        value: `${metric.used === null ? EMPTY : fmtCount(metric.used)} / ${fmtCount(
          metric.total
        )}`,
      }
    : metric;

  return (
    <StatTile
      label="Strategy slots"
      metric={slotMetric}
      format={(v) => v}
      hint={metric.note || metric.message || "One staked Supernova unlocks one strategy slot."}
    />
  );
};

/**
 * XP — the one tile that renames itself.
 *
 * A member's XP is banked (from closed stakes) plus accruing (on open ones).
 * Both halves are real now: the accruing one from the open lots, the banked one
 * rebuilt from the staking contract's event log (supernovaClosedLots). The tile
 * reads "XP balance" once it holds both, and falls back to "XP accrued" — with
 * the missing half named underneath — whenever the banked half is unknown,
 * because accrual alone is not a lifetime total.
 *
 * THE FOOTNOTE IS WHATEVER IS STILL MISSING, and buildXpBalance decides which:
 * the banked half absent entirely, or present but short by the closed lots the
 * log scan couldn't rebuild. Silence means the figure is complete — so this
 * component renders `bankedNote` rather than deciding for itself. When the
 * staking engine supplies its own combined total the note is null by
 * construction, since that figure already contains both halves.
 *
 * `xp` is a buildXpBalance() view. Without one we fall back to summary.xp, which
 * is the engine-or-nothing metric — the pre-SUP-7 behaviour.
 */
const XpTile = ({ xp, fallback }) => {
  const view = xp || null;
  const metric = view || fallback;
  /* `bankedNote` is null exactly when the figure is complete — including the
     engine branch, whose single total already contains both halves and where a
     "banked isn't connected" line would be a false disclaimer. Deciding that
     here from `bankedKnown` was the old bug: the stub is unknown even when the
     engine's combined total is what's on screen. */
  const footnote = view && view.status === METRIC.READY ? view.bankedNote : null;

  return (
    <StatTile
      label={view ? view.label : "XP balance"}
      metric={metric}
      format={fmtXp}
      testId="supernova-xp-tile"
      footnote={footnote}
      hint={
        metric.note ||
        metric.message ||
        "XP is an in-app points balance with no monetary value."
      }
    />
  );
};

const SupernovaStatStrip = ({ summary, xp = null }) => (
  <div className="sn-stats" data-testid="supernova-stat-strip">
    <StatTile label="Owned" metric={summary.owned} />
    <StatTile label="Staked" metric={summary.staked} />
    <SlotTile metric={summary.slots} />
    <XpTile xp={xp} fallback={summary.xp} />
  </div>
);

export default SupernovaStatStrip;
